import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export const POISApi = createApi({
	reducerPath: "POIS",
	baseQuery: fetchBaseQuery({
		baseUrl: process.env.REACT_APP_BACKEND2_URL,
		credentials: "include", // Applies to all requests
		prepareHeaders: (headers) => {
			const token = localStorage.getItem("token");
			if (token) {
				headers.set("Authorization", `Bearer ${token}`);
			}
			return headers;
		},
	}),
	endpoints: (builder) => ({
		getMapPOIS: builder.query({
			query: (val) => {
				const search = val.search ? `&search=${val.search}` : "";
				const status = val.status && `?status=${val.status}`;
				const queryString = val.queryString
					? `&${val.queryString.substring(1)}`
					: "";
				return `v1/Pois${status}${queryString}${search}`;
			},
		}),
		getAllPOIs: builder.query({
			query: (val) => {
				const page = val.page ?? 1;
				const limit = val.limit ?? 10;
				const sortBy = val.sortby ? `&sortBy=${val.sortby}` : "";
				const sortOrder = val.sortorder ? `&sortOrder=${val.sortorder}` : "";
				const search = val.search ? `&search=${val.search}` : "";
				const status = val.status ? `&status=${val.status}` : "";
				const queryString = val.queryString
					? `&${val.queryString.substring(1)}`
					: "";
				return `/v1/Pois?page=${page}&limit=${limit}${search}${sortBy}${sortOrder}${queryString}${status}`;
			},
		}),
		getDropdownPOIs: builder.query({
			query: () => "/v1/Pois?status=true",
		}),
		getSpecificPOI: builder.query({
			query: (id) => `/v1/pois/${id}`,
		}),
		addPOI: builder.mutation({
			query: (val) => {
				const formData = new FormData();

				const singleFileFields = [
					"layout",
					"contractorLogo",
					"customer1Logo",
					"customer2Logo",
					"consultantLogo",
				];

				const multiFileField = "attachments";

				const appendFormData = (data, parentKey = "") => {
					for (const key in data) {
						if (data[key] === null || data[key] === undefined) continue;

						const fullKey = parentKey ? `${parentKey}[${key}]` : key;
						const value = data[key];

						// -----------------------------------
						// MULTI FILE FIELD (ATTACHMENTS)
						// -----------------------------------
						if (key === multiFileField) {
							if (Array.isArray(value) && value.length > 0) {
								value.forEach((item) => {
									const file = item.originFileObj ?? item;
									formData.append(fullKey, file); // binary
								});
							} else {
							}
							continue;
						}

						// -----------------------------------
						// SINGLE FILE FIELDS (LOGOS)
						// -----------------------------------
						if (singleFileFields.includes(key)) {
							if (Array.isArray(value) && value.length > 0) {
								const file = value[0].originFileObj ?? value[0];
								formData.append(fullKey, file); // binary
							} else {
							}
							continue;
						}
						// -----------------------------------
						// NON-FILE NORMAL ARRAYS (forms, ids, labels...)
						// -----------------------------------
						if (
							Array.isArray(value) &&
							key !== multiFileField &&
							!singleFileFields.includes(key)
						) {
							formData.append(fullKey, JSON.stringify(value)); // forms → "[295]"
							continue;
						}

						// -----------------------------------
						// RECURSE NESTED OBJECTS
						// -----------------------------------
						if (typeof value === "object" && !(value instanceof File)) {
							appendFormData(value, fullKey);
							continue;
						}

						// -----------------------------------
						// PRIMITIVE VALUES
						// -----------------------------------
						formData.append(fullKey, value);
					}
				};

				appendFormData(val);

				return {
					url: `/v1/pois`,
					method: "POST",
					body: formData,
				};
			},
		}),

		editPOI: builder.mutation({
			query: ({ val, id }) => {
				const formData = new FormData();

				const singleFileFields = [
					"contractorLogo",
					"customer1Logo",
					"customer2Logo",
					"consultantLogo",
				];

				const multiFileField = "attachments";

				const appendFormData = (data, parentKey = "") => {
					for (const key in data) {
						const fullKey = parentKey ? `${parentKey}[${key}]` : key;
						const value = data[key];

						// -----------------------------------
						// MULTI FILE FIELD (ATTACHMENTS)
						// -----------------------------------
						if (key === multiFileField) {
							if (Array.isArray(value) && value.length > 0) {
								value.forEach((item) => {
									const file = item.originFileObj ?? item;

									// IMPORTANT: Only send real files on edit
									if (file instanceof File) {
										formData.append(fullKey, file);
									}
								});
							}
							continue; // skip sending empty arrays for edit
						}

						// -----------------------------------
						// SINGLE FILE FIELDS (LOGOS)
						// -----------------------------------
						if (singleFileFields.includes(key)) {
							if (value === null) {
								formData.append(fullKey, ""); // or send "null" depending on backend
							}
							if (Array.isArray(value) && value.length > 0) {
								const file = value[0].originFileObj ?? value[0];
								if (file instanceof File) {
									formData.append(fullKey, file); // new file
								} else if (file.url) {
									formData.append(fullKey, file.url); // send existing file URL if backend expects it
								}
							}
							continue;
						}

						// -----------------------------------
						// NON-FILE NORMAL ARRAYS (forms, ids, labels...)
						// -----------------------------------
						if (
							Array.isArray(value) &&
							key !== multiFileField &&
							!singleFileFields.includes(key)
						) {
							formData.append(fullKey, JSON.stringify(value));
							continue;
						}

						// -----------------------------------
						// RECURSE NESTED OBJECTS
						// -----------------------------------
						if (typeof value === "object" && !(value instanceof File)) {
							appendFormData(value, fullKey);
							continue;
						}

						// -----------------------------------
						// PRIMITIVE VALUES
						// -----------------------------------
						formData.append(fullKey, value);
					}
				};

				appendFormData(val);

				return {
					url: `/v1/pois/${id}`,
					method: "PATCH",
					body: formData,
				};
			},
		}),

		deletePOI: builder.mutation({
			query: (id) => ({
				url: `/v1/pois/${id}`,
				method: "DELETE",
			}),
		}),
		getPOIProjects: builder.query({
			query: () => {
				return `v1/pois/dropdown/projects`;
			},
		}),
		getPOIZonesByParentId: builder.query({
			query: ({ id }) => {
				return `v1/pois/dropdown/zones?parentId=${id}`;
			},
		}),
		getPOIZones: builder.query({
			query: () => {
				return `v1/pois/dropdown/zones`;
			},
		}),
		getPOIBuildings: builder.query({
			query: ({ id }) => {
				return `v1/pois/dropdown/levels?parentId=${id}`;
			},
		}),
		getPOIForm: builder.query({
			query: ({ category, poiId }) => {
				return `v1/pois/forms/by-pois?category=${category}&poiType=project&poiIds=${poiId}`;
			},
		}),
	}),
});

export const {
	useGetAllPOIsQuery,
	useGetMapPOISQuery,
	useGetSpecificPOIQuery,
	useAddPOIMutation,
	useEditPOIMutation,
	useDeletePOIMutation,
	useGetPOIProjectsQuery,
	useGetPOIBuildingsQuery,
	useGetPOIZonesQuery,
	useGetPOIZonesByParentIdQuery,
	useGetPOIFormQuery,
	useGetDropdownPOIsQuery,
} = POISApi;
